import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, message, Card, Row, Col, Input, Switch, Modal, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getUsers, updateUserStatus, deleteUser } from '../services/admin';
import type { UserInfo } from '../services/admin';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import { useAuth } from '../contexts/AuthContext';

interface UserListResponse {
  list: UserInfo[];
  total: number;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [walletStatus, setWalletStatus] = useState<number | undefined>(undefined);

  // 获取用户列表
  const fetchUsers = async (page?: number, size?: number) => {
    try {
      setLoading(true);
      const params = { 
        page: page || currentPage, 
        pageSize: size || pageSize,
        keyword: searchText || undefined,
        wallet_status: walletStatus,
        uid: user?.level_id === 1 ? undefined : user?.id
      };
      
      const response = await getUsers(params);
      
      if (response?.code === 0 && response.data) {
        setUsers(response.data.list);
        setTotal(response.data.total);
      } else {
        message.error(response?.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('获取用户列表失败：网络错误');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('UserManagement component mounted');
    fetchUsers();
  }, []);

  useEffect(() => {
    console.log('Users state updated:', users);
    console.log('Current pagination:', { current: currentPage, pageSize: pageSize });
  }, [users, currentPage, pageSize]);

  // 监听钱包状态变化
  useEffect(() => {
    if (walletStatus !== undefined) {
      setCurrentPage(1);
      fetchUsers(1, pageSize);
    }
  }, [walletStatus]);

  // 处理表格变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    if (newPagination.current && newPagination.pageSize) {
      setCurrentPage(newPagination.current);
      setPageSize(newPagination.pageSize);
      fetchUsers(newPagination.current, newPagination.pageSize);
    }
  };

  // 处理搜索
  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(1, pageSize);
  };

  // 处理重置
  const handleReset = () => {
    setSearchText('');
    setWalletStatus(undefined);
    setCurrentPage(1);
    fetchUsers(1, pageSize);
  };

  // 处理状态切换
  const handleStatusChange = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const response = await updateUserStatus(id, newStatus);
      if (response && response.code === 0) {
        message.success('状态更新成功');
        fetchUsers();
      } else {
        message.error(response?.message || '状态更新失败');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      message.error('状态更新失败：网络错误');
    }
  };

  // 处理删除用户
  const handleDelete = async (id: number) => {
    try {
      await deleteUser(id);
      message.success('删除成功');
      fetchUsers();
    } catch (error) {
      console.error('删除用户失败:', error);
      message.error('删除失败');
    }
  };

  // 显示删除确认对话框
  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个用户吗？此操作不可恢复。',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => handleDelete(id),
    });
  };

  const columns: ColumnsType<UserInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'A地址',
      dataIndex: 'a_address',
      key: 'a_address',
      width: 400,
      render: (address) => (
        <div style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
          {address || '-'}
        </div>
      ),
    },
    {
      title: 'B地址',
      dataIndex: 'b_address',
      key: 'b_address',
      width: 400,
      render: (address) => (
        <div style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
          {address || '-'}
        </div>
      ),
    },
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 80,
    },
    {
      title: '金额',
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      render: (balance) => balance || '0.000000',
    },
    {
      title: '添加时间',
      dataIndex: 'addtime',
      key: 'addtime',
      width: 150,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (addtime) => addtime ? new Date(addtime * 1000).toLocaleString() : '-',
    },
    {
      title: '钱包状态',
      dataIndex: 'wallet_status',
      key: 'wallet_status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '已授权' : '未授权'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: () => null,
    },
  ];

  return (
    <Card>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Input
            placeholder="输入钱包地址搜索"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            suffix={
              <SearchOutlined 
                style={{ color: 'rgba(0,0,0,.45)', cursor: 'pointer' }}
                onClick={handleSearch}
              />
            }
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder="选择钱包状态"
            style={{ width: '100%' }}
            allowClear
            value={walletStatus}
            onChange={(value) => setWalletStatus(value)}
          >
            <Select.Option value={1}>已授权</Select.Option>
            <Select.Option value={0}>未授权</Select.Option>
          </Select>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button onClick={handleReset} icon={<ReloadOutlined />}>
            重置
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1000 }}
        size="small"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default UserManagement; 